import React from "react";
import { useField, useFormikContext } from "formik";
import { DatePicker } from "antd";
import dayjs from "dayjs";

const getFieldCSSClasses = (touched, errors) => {
  const classes = ["form-control"];

  if (touched && errors) {
    classes.push("is-invalid");
  }

  if (touched && !errors) {
    classes.push("is-valid");
  }

  return classes.join(" ");
};

/*
 * Convert API/Formik date into a LOCAL Dayjs value.
 *
 * Important:
 *
 * API:
 *   "2026-08-11 03:00:00"
 *
 * We explicitly convert it to:
 *   "2026-08-11T03:00:00"
 *
 * This prevents timezone/UTC interpretation.
 */
const parseLocalDate = (value) => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const date = dayjs(value);

    return date.isValid() ? date : null;
  }

  if (typeof value === "string") {
    /*
     * API format:
     * YYYY-MM-DD HH:mm:ss
     *
     * Convert space to T so it is explicitly
     * treated as a local datetime.
     */
    const normalized = value.includes("T") ? value : value.replace(" ", "T");

    const date = dayjs(normalized);

    return date.isValid() ? date : null;
  }

  return null;
};

export function DatePickerField({ label, showTime, ...props }) {
  const { setFieldValue, errors, touched } = useFormikContext();

  const [field] = useField(props);

  const dateValue = parseLocalDate(field.value);

  /*
   * User selects a date/time.
   *
   * Store local datetime in Formik:
   *
   * 2026-08-11T03:00:00
   *
   * NOT:
   *
   * 2026-08-11T00:00:00.000Z
   */
  const handleChange = (value) => {
    if (!value) {
      setFieldValue(field.name, "", true);

      return;
    }

    const localDateTime = value.format("YYYY-MM-DDTHH:mm:ss");

    setFieldValue(field.name, localDateTime, true);
  };

  return (
    <>
      <div>{label && <label>{label}</label>}</div>

      <DatePicker
        {...props}
        /*
         * IMPORTANT:
         * Don't allow props.value to override
         * the Formik value.
         */
        value={dateValue}
        needConfirm={false}
        className={getFieldCSSClasses(touched[field.name], errors[field.name])}
        style={{
          width: "100%",
        }}
        onChange={handleChange}
        format={showTime ? "YYYY-MM-DD hh:mm a" : "YYYY-MM-DD"}
        showTime={
          showTime
            ? {
                format: "hh:mm a",
              }
            : false
        }
      />

      {errors[field.name] && touched[field.name] ? (
        <div className="invalid-datepicker-feedback">
          {errors[field.name].toString()}
        </div>
      ) : (
        <div className="feedback">
          Use {showTime ? "yyyy-mm-dd hh:mm" : "yyyy-mm-dd"} format
        </div>
      )}
    </>
  );
}
